import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, ArrowRight, PieChart, BarChart3, Download, Send } from 'lucide-react';
import GradientText from '../components/GradientText';

const Step = ({ children }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.3 }}>
    {children}
  </motion.div>
);

const industries = ['Retail', 'Finance', 'Healthcare', 'Manufacturing', 'Legal', 'Other'];

const effects = {
  supportLoss: 0.35,
  opsHoursLoss: 0.30,
  reportDelay: 0.60,
  contentLossFactor: 1.5,
  churnLoss: 0.07
};

const CostOfInaction = () => {
  const [step, setStep] = useState(0);
  const [inputs, setInputs] = useState({
    industry: '',
    employeesManual: '',
    hourlyCost: '',
    supportTickets: '',
    supportCostPerTicket: 10,
    reportDays: 3,
    contentLaunchesPerYear: 12,
    avgContentValue: 1000,
    annualRevenue: ''
  });

  const [results, setResults] = useState(null);
  const [lead, setLead] = useState({ name: '', email: '', company: '' });
  const resultsRef = useRef(null);

  const handle = (f) => (e) => setInputs((p) => ({ ...p, [f]: e.target.type === 'number' || e.target.type === 'range' ? Number(e.target.value) : e.target.value }));
  const next = () => setStep((s) => Math.min(s + 1, 4));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const compute = () => {
    const supportLoss = (inputs.supportTickets || 0) * (inputs.supportCostPerTicket || 0) * 12 * effects.supportLoss;
    const opsLoss = (inputs.employeesManual || 0) * 52 *  (8 /*hrs/wk*/ ) * effects.opsHoursLoss * (inputs.hourlyCost || 0);
    const reportingLoss = 12 * ((inputs.reportDays || 0) * 8) * effects.reportDelay * (inputs.hourlyCost || 0);
    const innovationLoss = (inputs.contentLaunchesPerYear || 0) * (inputs.avgContentValue || 0) * effects.contentLossFactor;
    const churnLoss = (inputs.annualRevenue ? inputs.annualRevenue * effects.churnLoss : 0);

    const total = Math.round(supportLoss + opsLoss + reportingLoss + innovationLoss + churnLoss);
    setResults({ total, breakdown: { supportLoss: Math.round(supportLoss), opsLoss: Math.round(opsLoss), reportingLoss: Math.round(reportingLoss), innovationLoss: Math.round(innovationLoss), churnLoss: Math.round(churnLoss) } });
    setStep(4);
  };

  const downloadPdf = () => {
    if (!resultsRef.current) return;
    const printWin = window.open('', 'PRINT', 'height=800,width=1000');
    if (!printWin) return;
    printWin.document.write(`<!doctype html><html><head><title>Cost of Inaction Report</title><style>body{font-family:Inter,Arial,sans-serif;background:#0f172a;color:#e5e7eb;padding:24px} .card{background:#111827;border:1px solid #374151;border-radius:12px;padding:16px;margin-bottom:12px} h1,h2,h3{margin:0 0 12px} .row{display:flex;gap:12px} .col{flex:1} .kpi{font-size:28px;font-weight:800;color:#fca5a5}</style></head><body>`);
    printWin.document.write('<h1>AI Cost of Inaction Report</h1>');
    printWin.document.write(resultsRef.current.innerHTML);
    printWin.document.write('</body></html>');
    printWin.document.close();
    printWin.focus();
    printWin.print();
    printWin.close();
  };

  const submitLead = async () => {
    try {
      const endpoint = process.env.REACT_APP_LEADS_ENDPOINT;
      if (!endpoint) {
        alert('Report sent! (Demo mode: configure REACT_APP_LEADS_ENDPOINT for real submissions)');
        return;
      }
      const payload = { type: 'coi_report', lead, inputs, results };
      const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error('Failed');
      alert('Report sent! Check your email.');
    } catch (e) {
      alert('Could not submit right now. Please try again later.');
    }
  };

  return (
    <section className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white">
      <div className="py-16 px-6 md:px-12 max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 backdrop-blur-xl rounded-full border border-blue-400/30 mb-4">
            <AlertTriangle className="w-5 h-5 text-blue-300" />
            <span className="font-semibold text-blue-200">AI Cost of Inaction Calculator</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black mb-4">Your <GradientText>Cost of Inaction</GradientText></h1>
          <p className="text-blue-100 max-w-3xl mx-auto">Estimate how much you could be losing annually by delaying AI adoption.</p>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
          <div className="flex items-center justify-between text-sm text-slate-300 mb-6">
            <div>Step {step + 1} of 5</div>
            <div className="h-2 bg-white/10 rounded w-40">
              <div className="h-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded" style={{ width: `${((step + 1) / 5) * 100}%` }} />
            </div>
          </div>

          <AnimatePresence mode="wait">
            {step === 0 && (
              <Step>
                <h2 className="text-2xl font-bold mb-4">Company Profile</h2>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm mb-2">Industry</label>
                    <select value={inputs.industry} onChange={handle('industry')} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg">
                      <option value="">Select</option>
                      {industries.map((i) => (<option key={i} value={i}>{i}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm mb-2">Annual Revenue (optional)</label>
                    <input type="number" value={inputs.annualRevenue} onChange={handle('annualRevenue')} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg" placeholder="e.g., 10000000" />
                  </div>
                  <div>
                    <label className="block text-sm mb-2">Hourly Cost ($)</label>
                    <input type="number" value={inputs.hourlyCost} onChange={handle('hourlyCost')} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg" placeholder="e.g., 40" />
                  </div>
                </div>
                <div className="flex gap-3 mt-6"><button onClick={next} className="px-6 py-2 bg-blue-600 rounded flex items-center gap-2">Next <ArrowRight className="w-4 h-4" /></button></div>
              </Step>
            )}

            {step === 1 && (
              <Step>
                <h2 className="text-2xl font-bold mb-4">Support & Operations</h2>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm mb-2">Support tickets per month</label>
                    <input type="number" value={inputs.supportTickets} onChange={handle('supportTickets')} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg" placeholder="e.g., 2000" />
                  </div>
                  <div>
                    <label className="block text-sm mb-2">Cost per ticket ($)</label>
                    <input type="number" value={inputs.supportCostPerTicket} onChange={handle('supportCostPerTicket')} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg" placeholder="e.g., 10" />
                  </div>
                  <div>
                    <label className="block text-sm mb-2">Employees in manual roles</label>
                    <input type="number" value={inputs.employeesManual} onChange={handle('employeesManual')} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg" placeholder="e.g., 50" />
                  </div>
                </div>
                <div className="flex gap-3 mt-6"><button onClick={back} className="px-4 py-2 border border-white/20 rounded">Back</button><button onClick={next} className="px-6 py-2 bg-blue-600 rounded">Next</button></div>
              </Step>
            )}

            {step === 2 && (
              <Step>
                <h2 className="text-2xl font-bold mb-4">Reporting & Innovation</h2>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm mb-2">Days per report</label>
                    <input type="range" min={0} max={10} value={inputs.reportDays} onChange={handle('reportDays')} className="w-full" />
                    <div className="text-sm text-slate-300">{inputs.reportDays} days</div>
                  </div>
                  <div>
                    <label className="block text-sm mb-2">Launches per year</label>
                    <input type="number" value={inputs.contentLaunchesPerYear} onChange={handle('contentLaunchesPerYear')} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg" placeholder="e.g., 12" />
                  </div>
                  <div>
                    <label className="block text-sm mb-2">Avg. value per launch ($)</label>
                    <input type="number" value={inputs.avgContentValue} onChange={handle('avgContentValue')} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg" placeholder="e.g., 1000" />
                  </div>
                </div>
                <div className="flex gap-3 mt-6"><button onClick={back} className="px-4 py-2 border border-white/20 rounded">Back</button><button onClick={next} className="px-6 py-2 bg-blue-600 rounded">Next</button></div>
              </Step>
            )}

            {step === 3 && (
              <Step>
                <h2 className="text-2xl font-bold mb-4">See Your Cost of Inaction</h2>
                <button onClick={compute} className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg font-semibold flex items-center gap-2">Calculate <ArrowRight className="w-4 h-4" /></button>
              </Step>
            )}

            {step === 4 && results && (
              <Step>
                <h2 className="text-2xl font-bold mb-2">Results</h2>
                <p className="text-slate-300 mb-6">Estimated annual loss by delaying AI adoption.</p>

                <div ref={resultsRef} className="bg-white/5 rounded-xl p-5 border border-white/10 text-center mb-6">
                  <div className="text-3xl md:text-5xl font-extrabold text-red-300 mb-1">${results.total.toLocaleString()}</div>
                  <div className="text-sm text-slate-300">Annual Cost of Inaction</div>
                
                  <div className="grid md:grid-cols-2 gap-6 mt-6 text-left">
                    <div className="card">
                      <div className="flex items-center gap-2 mb-3"><BarChart3 className="w-4 h-4" /><span className="font-semibold">Breakdown</span></div>
                      {Object.entries(results.breakdown).map(([k, v]) => (
                        <div key={k} className="mb-3">
                          <div className="flex justify-between text-sm text-slate-300 mb-1"><span className="capitalize">{k.replace('Loss','')}</span><span>${v.toLocaleString()}</span></div>
                          <div className="h-2 bg-white/10 rounded"><div className="h-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded" style={{ width: `${Math.min(100, (v / results.total) * 100)}%` }} /></div>
                        </div>
                      ))}
                    </div>
                    <div className="card">
                      <div className="flex items-center gap-2 mb-3"><PieChart className="w-4 h-4" /><span className="font-semibold">3-Year Projection</span></div>
                      <div className="text-slate-300 text-sm">If no AI adoption, projected 3-year loss: <span className="text-white font-semibold">${(results.total * 3).toLocaleString()}</span></div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <h3 className="font-semibold mb-2">Get Full CoI Report (PDF)</h3>
                  <div className="grid md:grid-cols-3 gap-3">
                    <input placeholder="Name" className="px-3 py-2 bg-white/10 border border-white/20 rounded" value={lead.name} onChange={(e)=>setLead({...lead,name:e.target.value})} />
                    <input placeholder="Email" className="px-3 py-2 bg-white/10 border border-white/20 rounded" value={lead.email} onChange={(e)=>setLead({...lead,email:e.target.value})} />
                    <input placeholder="Company" className="px-3 py-2 bg-white/10 border border-white/20 rounded" value={lead.company} onChange={(e)=>setLead({...lead,company:e.target.value})} />
                  </div>
                  <div className="flex gap-3 mt-3">
                    <button onClick={downloadPdf} className="px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 rounded inline-flex items-center gap-2"><Download className="w-4 h-4" /> Download PDF</button>
                    <button onClick={submitLead} className="px-5 py-2 border border-white/20 rounded inline-flex items-center gap-2"><Send className="w-4 h-4" /> Send My Report</button>
                  </div>
                </div>
              </Step>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default CostOfInaction;


